import { useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { AUDIENCE_FIELD_KEYS, BRAND_LIMITS } from '@mg/shared';
import { Button } from '../../../components/ui/Button';
import { AudienceCard, audienceTitle } from '../../brand/fields/AudienceCard';
import { useBrandDraft } from '../draft/BrandDraftContext';
import { AUDIENCES_ADD_ID, audienceFieldId, audienceHasError } from '../draft/brandDraft';
import { WizardAudienceField } from '../steps/WizardField';
import { AUDIENCES_COPY } from '../wizardCopy';

/**
 * Step 7, Audiences (spec:267; web.md §5.7): Add audience at the top with "{n} of 10 audiences", then
 * one collapsible card per audience. A new card goes first, open, with the focus in its identity; an
 * imported card arrives closed, after the others. Remove audience takes a card out of the draft; it is
 * deleted only when the step is saved. A card with a field over its limit stays open, so the error shows
 * and Next can focus it.
 */
export function AudiencesStep({ disabled = false }: { disabled?: boolean }) {
  const { state, dispatch } = useBrandDraft();
  const [open, setOpen] = useState<ReadonlySet<string>>(() => new Set());
  // Where the focus goes after the next render: a new card's identity, or Add audience after a removal.
  const [focusTarget, setFocusTarget] = useState<string | null>(null);

  useEffect(() => {
    if (focusTarget === null) return;
    document.getElementById(focusTarget)?.focus();
    setFocusTarget(null);
  }, [focusTarget]);

  const count = state.audiences.length;
  const full = count >= BRAND_LIMITS.audiences;

  const add = () => {
    const key = `new-${state.nextAudienceKey}`;
    dispatch({ type: 'addAudience' });
    setOpen((prev) => new Set(prev).add(key));
    setFocusTarget(audienceFieldId(key, 'identity'));
  };

  const toggle = (key: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const remove = (key: string) => {
    dispatch({ type: 'removeAudience', key });
    setFocusTarget(AUDIENCES_ADD_ID);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <Button id={AUDIENCES_ADD_ID} type="button" variant="secondary" disabled={disabled || full} onClick={add}>
          <Plus className="h-4 w-4" aria-hidden />
          {AUDIENCES_COPY.add}
        </Button>
        <p aria-live="polite" className="text-sm text-ink-muted">
          {full ? AUDIENCES_COPY.full : AUDIENCES_COPY.count(count)}
        </p>
      </div>
      {state.audiences.map((card, index) => (
        <AudienceCard
          key={card.key}
          mode="edit"
          title={audienceTitle(typeof card.fields.identity.value === 'string' ? card.fields.identity.value : null, 'edit', index)}
          expanded={open.has(card.key) || audienceHasError(card)}
          onToggle={() => toggle(card.key)}
          onRemove={() => remove(card.key)}
          removeDisabled={disabled}
        >
          {AUDIENCE_FIELD_KEYS.map((key) => (
            <WizardAudienceField key={key} cardKey={card.key} fieldKey={key} disabled={disabled} />
          ))}
        </AudienceCard>
      ))}
    </div>
  );
}
